import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Avatar, Button, Spinner } from '@components/ui';
import { useTaskComments, useCreateComment, useDeleteComment, useUpdateComment } from '@hooks';
import useAuthStore from '@store/useAuthStore';

export default function CommentsPanel({ taskId }) {
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const commentsEndRef = useRef(null);
  const currentUser = useAuthStore((state) => state.user);

  const { data: commentsData, isLoading } = useTaskComments(taskId);
  const createComment = useCreateComment();
  const deleteComment = useDeleteComment();
  const updateComment = useUpdateComment();

  const comments = commentsData?.data || [];

  useEffect(() => {
    // Scroll to bottom when new comments are added
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    await createComment.mutateAsync({
      taskId,
      content: newComment.trim(),
    });
    setNewComment('');
  };

  const handleEdit = (comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return;

    await updateComment.mutateAsync({
      id: editingId,
      data: { content: editContent.trim() },
    });
    setEditingId(null);
    setEditContent('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este comentario?')) {
      await deleteComment.mutateAsync(id);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;

    return d.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase() || '?';
  };

  const renderContent = (content) => {
    // Highlight @mentions
    const parts = content.split(/(@\w+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return (
          <span
            key={i}
            style={{
              color: '#667eea',
              fontWeight: '500',
              backgroundColor: '#667eea15',
              padding: '1px 4px',
              borderRadius: '4px',
            }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
          Comentarios ({comments.length})
        </h4>
      </div>

      {/* Comments list */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          maxHeight: '300px',
          marginBottom: '16px',
        }}
      >
        {comments.length === 0 ? (
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              border: '1px dashed #e2e8f0',
            }}
          >
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
              No hay comentarios aún
            </p>
            <p style={{ fontSize: '12px', color: '#94a3b8', margin: '4px 0 0 0' }}>
              Sé el primero en comentar
            </p>
          </div>
        ) : (
          comments.map((comment) => (
            <div
              key={comment.id}
              style={{
                display: 'flex',
                gap: '12px',
              }}
            >
              <Avatar initials={getInitials(comment.user?.name)} size="sm" />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
                    {comment.user?.name || 'Usuario'}
                  </span>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                    {formatDate(comment.createdAt)}
                  </span>
                  {comment.updatedAt !== comment.createdAt && (
                    <span style={{ fontSize: '10px', color: '#cbd5e1' }}>(editado)</span>
                  )}
                </div>

                {editingId === comment.id ? (
                  <div>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '13px',
                        resize: 'none',
                        outline: 'none',
                      }}
                      rows={2}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={handleSaveEdit}
                        loading={updateComment.isPending}
                      >
                        Guardar
                      </Button>
                      <Button size="sm" variant="secondary" onClick={handleCancelEdit}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p
                      style={{
                        fontSize: '13px',
                        color: '#475569',
                        margin: 0,
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {renderContent(comment.content)}
                    </p>

                    {currentUser?.id === comment.userId && (
                      <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
                        <button
                          onClick={() => handleEdit(comment)}
                          style={{
                            fontSize: '11px',
                            color: '#64748b',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(comment.id)}
                          style={{
                            fontSize: '11px',
                            color: '#ef4444',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          Eliminar
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={commentsEndRef} />
      </div>

      {/* New comment form */}
      <form onSubmit={handleSubmit} style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Avatar initials={getInitials(currentUser?.name)} size="sm" />
          <div style={{ flex: 1 }}>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Escribe un comentario... Usa @nombre para mencionar"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                fontSize: '13px',
                resize: 'none',
                outline: 'none',
                transition: 'border-color 0.15s',
              }}
              rows={2}
              onFocus={(e) => (e.target.style.borderColor = '#667eea')}
              onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={createComment.isPending}
                disabled={!newComment.trim()}
              >
                Comentar
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

CommentsPanel.propTypes = {
  taskId: PropTypes.number.isRequired,
};
